import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter } from '@angular/router';
import { NzModalModule } from 'ng-zorro-antd/modal';

import { routes } from './app.routes';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import {
  credencialesInterceptor,
  proveerSesion,
  sesionExpiradaInterceptor,
} from '@intelishs-group/session-core';
import { NotifyService } from '@intelishs-group/notify-service';
import { proveerI18n, proveerOverlays } from '@intelishs-group/ui';

/** La sesión de plataforma. */
const sesionBase = {
  identidadUrl: environment.identidadUrl,
  // Se llama `logoutUrl` por historia —de cuando el único camino era cerrar la
  // sesión del SSO— pero es el login CENTRAL, a donde se va a INICIAR sesión.
  loginUrl: environment.logoutUrl,
} as const;

export const appConfig: ApplicationConfig = {
  providers: [
    // Angular 21 arranca SIN zone.js por defecto. Esto conserva la detección de
    // cambios con zone.js que la app tenía en Angular 20 (zone.js sigue en los
    // polyfills). Es lo mismo que agrega la migración oficial de `ng update` 21
    // (`bootstrap-options-migration`).
    provideZoneChangeDetection(),
    provideRouter(routes),
    provideBrowserGlobalErrorListeners(),
    // `credencialesInterceptor` va PRIMERO y no es opcional: la aplicación no
    // manda el token, lo manda el navegador como cookie y sólo si la petición
    // lleva credenciales. Sustituye al `credentialsInterceptor` local, que hacía
    // exactamente lo mismo —`withCredentials: true` en todas las peticiones—.
    provideHttpClient(withInterceptors([credencialesInterceptor, sesionExpiradaInterceptor])),
    provideAnimationsAsync(),
    // `NzModalService` NO es `providedIn: 'root'`: lo provee `NzModalModule`. Aqui
    // nadie lo proveia — llegaba DE REBOTE del `<intelishs-file-viewer>` que
    // `app.html` monta en la raiz, porque ese componente importa el modulo en su
    // propio `imports` y eso iza el provider al injector de entorno.
    //
    // Funciona hasta que alguien quita el visor de `app.html`, y entonces se cae
    // algo que no tiene nada que ver con ese cambio. A `ffacturacion` le paso
    // exactamente eso al borrar su `modal-pdf-viewer` local: `tour-button` se
    // quedo sin el servicio que inyecta y que nunca importo el modulo.
    importProvidersFrom(NzModalModule),
    // Los overlays del CDK vuelven al apilamiento por z-index (ui >= 5.2.7). Sin
    // esto, desde Angular 21 un modal abierto tapa los avisos de SweetAlert y el
    // velo de carga: salen detras, no se pueden pulsar y la pantalla parece colgada.
    proveerOverlays(),
    // El idioma en una linea (ui >= 5.2.10): registra es-MX, fija LOCALE_ID y da a
    // ng-zorro el locale con el que formatea sus pickers. Sustituye a
    // registerLocaleData(es) + provideNzI18n(es_ES), que formateaban como Espana.
    proveerI18n(),
    // `LAYOUT_ENV` se fue con `<layout-main>`: el shell nuevo no lee nada del
    // inyector y el nombre del sistema entra por `[marca]` en `App`.

    // Sustituye a `provideSession({ tokenName: 'sinay-token' })`, que sólo
    // guardaba un JSON en `sessionStorage` (lo llenaba el SSO viejo vía
    // `/Membership/GetTokenInSession`). Ahora la sesión la resuelve identidad,
    // llega en la cookie `sinay_acceso` y se carga ANTES de montar el primer
    // componente.
    //
    // El `inject()` del aviso FUNCIONA desde 5.0.5, y antes no. Esta flecha corre
    // después del `await` del inicializador, fuera del contexto de inyección, así
    // que lanzaba NG0203 y la librería se lo tragaba: el aviso no salía nunca y
    // nada lo delataba. Por eso había debajo un `SESION_CONFIG` con `useFactory`
    // que capturaba `NotifyService` y volvía a declarar toda la configuración.
    // Ya no hace falta: el servicio invoca el aviso con `runInInjectionContext`.
    proveerSesion({
      ...sesionBase,
      avisarSesionCaducada: () => inject(NotifyService).unauthorizedNotify(),
      // Sin `rutaSinPermiso`: quien no tiene el modulo va a `/`, y `inicioGuard` lo lleva a SU
      // primera pantalla.
    }),
    // ⚠️ Aquí había tres providers —`CLIENTE_CONFIG`, `COMPANIES_CONFIG` y
    // `CATALOG_CONFIG`— y esta aplicación no monta un solo componente de esas
    // tres librerías: su único rastro en todo `src/` era este archivo.
    //
    // Vienen del boilerplate, que los estampa en cada proyecto generado. Un
    // provider para una librería que no se usa no rompe nada —por eso llevaban
    // tanto— pero no es inofensivo: obliga a mantener tres dependencias y tres
    // bloques de configuración apuntando al backend, y le dice al que lee este
    // archivo que la aplicación habla con unos servicios que nunca llama.
  ],
};
